import React, { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Pagination from "../../components/common/Pagination";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchInquiries } from "../../store/slices/inquiriesSlice";
import { InquiryType } from "../../types/admin";
import {
  formatDateDot,
  formatInquiryType,
  INQUIRY_TYPE_OPTIONS,
} from "../../utils/admin";

type AnswerFilter = "all" | "pending" | "answered";

const InquiriesPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    list,
    totalCount,
    pendingCount,
    answeredCount,
    page,
    pageSize,
    loadingList,
    listError,
  } = useAppSelector((state) => state.inquiries);

  const [pageState, setPageState] = useState(1);
  const [answerFilter, setAnswerFilter] = useState<AnswerFilter>("all");
  const [inquiryType, setInquiryType] = useState<"" | InquiryType>("");
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    dispatch(
      fetchInquiries({
        page: pageState,
        page_size: 20,
        inquiry_type: inquiryType || undefined,
        q: query || undefined,
        is_answered:
          answerFilter === "all" ? undefined : answerFilter === "answered",
      })
    );
  }, [answerFilter, dispatch, inquiryType, pageState, query]);

  const onSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setQuery(searchInput.trim());
    setPageState(1);
  };

  const allCount = pendingCount + answeredCount || totalCount;

  return (
    <section className="admin-page inquiries-page">
      <h2 className="section-title">1:1 고객 문의 관리</h2>

      <div className="inquiries-toolbar">
        <div className="inquiry-status-tabs" role="tablist" aria-label="문의 상태">
          <button
            type="button"
            className={`inquiry-status-tab ${answerFilter === "all" ? "is-active" : ""}`}
            onClick={() => {
              setAnswerFilter("all");
              setPageState(1);
            }}
          >
            전체 {allCount}
          </button>
          <button
            type="button"
            className={`inquiry-status-tab inquiry-status-tab--pending ${
              answerFilter === "pending" ? "is-active" : ""
            }`}
            onClick={() => {
              setAnswerFilter("pending");
              setPageState(1);
            }}
          >
            답변대기 {pendingCount}
          </button>
          <button
            type="button"
            className={`inquiry-status-tab ${answerFilter === "answered" ? "is-active" : ""}`}
            onClick={() => {
              setAnswerFilter("answered");
              setPageState(1);
            }}
          >
            답변완료 {answeredCount}
          </button>
        </div>

        <div className="inquiries-toolbar__filters">
          <label className="inquiries-filter">
            <span>분류</span>
            <select
              value={inquiryType}
              onChange={(event) => {
                setInquiryType(event.target.value as "" | InquiryType);
                setPageState(1);
              }}
            >
              <option value="">전체</option>
              {INQUIRY_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <form className="inquiries-search" onSubmit={onSearch}>
            <span>검색</span>
            <div className="inquiries-search__box">
              <span className="inquiries-search__icon" aria-hidden>
                ⌕
              </span>
              <input
                type="search"
                placeholder="작성자, 제목으로 검색"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </div>
          </form>
        </div>
      </div>

      <div className="table-shell inquiries-table-shell">
        <table className="admin-table inquiries-table">
          <thead>
            <tr>
              <th>상태</th>
              <th>문의 유형</th>
              <th>제목</th>
              <th>작성자</th>
              <th>등록일</th>
            </tr>
          </thead>
          <tbody>
            {list.map((inquiry) => (
              <tr
                key={inquiry.inquiry_id}
                className="inquiries-table__row"
                onClick={() => navigate(`/admin/inquiries/${inquiry.inquiry_id}`)}
              >
                <td>
                  <span
                    className={`inquiry-badge ${
                      inquiry.is_answered ? "inquiry-badge--done" : "inquiry-badge--pending"
                    }`}
                  >
                    {inquiry.is_answered ? "답변완료" : "답변대기"}
                  </span>
                </td>
                <td className="inquiries-table__type">[{formatInquiryType(inquiry.inquiry_type)}]</td>
                <td className="text-left inquiries-table__title">{inquiry.title}</td>
                <td>{inquiry.user_name}</td>
                <td>{formatDateDot(inquiry.created_at)}</td>
              </tr>
            ))}
            {!loadingList && list.length === 0 && (
              <tr>
                <td className="empty-row" colSpan={5}>
                  등록된 문의가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {loadingList && <p className="loading-text">문의 목록을 불러오는 중입니다...</p>}
      {listError && <p className="error-text">{listError}</p>}
      <Pagination
        page={page || pageState}
        pageSize={pageSize || 20}
        totalCount={totalCount}
        onPageChange={setPageState}
      />
    </section>
  );
};

export default InquiriesPage;
